/**
 * Generic AKU action dialog (approve / reject / deprecate).
 * Eliminates ~100 lines of near-duplicate JSX in AKUV2Manager.js.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Archive } from 'lucide-react';

const VARIANTS = {
  approve: {
    Icon: CheckCircle,
    iconClass: 'text-green-600',
    title: 'AKU genehmigen (READY)',
    placeholder: 'Anmerkungen zur Genehmigung...',
    rows: 2,
    reasonLabel: 'Kommentar (optional)',
    confirmText: 'Genehmigen',
    confirmClass: 'bg-green-600 hover:bg-green-700',
    confirmVariant: 'default',
  },
  reject: {
    Icon: XCircle,
    iconClass: 'text-red-600',
    title: 'AKU ablehnen',
    placeholder: 'Warum wird dieses AKU abgelehnt?',
    rows: 3,
    reasonLabel: 'Begründung (erforderlich)',
    confirmText: 'Ablehnen',
    confirmClass: '',
    confirmVariant: 'destructive',
  },
  deprecate: {
    Icon: Archive,
    iconClass: 'text-purple-600',
    title: 'AKU als veraltet markieren',
    placeholder: 'Warum ist dieses AKU veraltet?',
    rows: 3,
    reasonLabel: 'Begründung (erforderlich)',
    confirmText: 'Als veraltet markieren',
    confirmClass: 'bg-purple-600 hover:bg-purple-700',
    confirmVariant: 'default',
  },
};

const AkuActionDialog = ({ type, open, onClose, reason, onReasonChange, onConfirm }) => {
  const variant = VARIANTS[type];
  if (!variant) return null;
  const { Icon, iconClass, title, placeholder, rows, reasonLabel, confirmText, confirmClass, confirmVariant } = variant;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconClass}`} />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{reasonLabel}</Label>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={placeholder}
              rows={rows}
              data-testid={`aku-action-${type}-reason`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`aku-action-${type}-cancel`}>
            Abbrechen
          </Button>
          <Button
            onClick={onConfirm}
            className={confirmClass}
            variant={confirmVariant}
            data-testid={`aku-action-${type}-confirm`}
          >
            <Icon className="w-4 h-4 mr-2" />
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AkuActionDialog;
